import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, Pencil, Check } from "lucide-react";

const BLANK_PHASE = (order) => ({
  phase: "NEW PHASE",
  block: "Block name",
  hybrid: "B",
  protocol: "",
  rest: "",
  metrics: "",
  color: "#0B1E36",
  order,
});

export default function CoachCalendar() {
  const [phases, setPhases] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/protocol-calendar");
    setPhases(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const updatePhase = (idx, k, v) => {
    const next = phases.map((p, i) => (i === idx ? { ...p, [k]: v } : p));
    setPhases(next);
  };
  const addPhase = () => setPhases([...phases, BLANK_PHASE((phases[phases.length - 1]?.order || 0) + 1)]);
  const removePhase = (idx) => setPhases(phases.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/teams/me/calendar", { phases });
      toast.success("Calendar saved for your team");
      setEditing(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Reset to ArmIQ's default annual calendar? Your custom phases will be removed.")) return;
    try {
      await api.delete("/teams/me/calendar");
      await load();
      setEditing(false);
      toast.success("Reset to default calendar");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="coach-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Module 3 · Table C</div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">
            Annual Protocol Calendar
          </h1>
          <p className="text-sm text-white/50 mt-2">Your team's phase plan. Edit any block, change colors, or add/remove phases — all changes scope to your tenant only.</p>
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <Button onClick={() => setEditing(true)} variant="outline" className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="calendar-edit-button">
              <Pencil className="h-4 w-4 mr-2" /> Edit calendar
            </Button>
          ) : (
            <>
              <Button onClick={resetDefault} variant="outline" className="border-white/15 text-white/70 bg-white/5 hover:bg-white/10" data-testid="calendar-reset-button">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset to default
              </Button>
              <Button onClick={save} disabled={saving} className="rounded-full px-6 text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="calendar-save-button">
                <Check className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save calendar"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {phases.map((p, idx) => (
          <div key={idx} className="rounded-xl ink-card overflow-hidden flex" data-testid={`phase-${p.order}`}>
            <div className="w-1.5" style={{ backgroundColor: p.color }} />
            <div className="flex-1 p-5 grid md:grid-cols-12 gap-4">
              {!editing ? (
                <>
                  <div className="md:col-span-3">
                    <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: p.color }}>{p.phase}</div>
                    <div className="font-display font-bold text-base mt-1 text-white">{p.block}</div>
                  </div>
                  <div className="md:col-span-2 text-sm">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Hybrid</div>
                    <div className="font-display font-black text-3xl mt-0.5 text-white">{p.hybrid}</div>
                  </div>
                  <div className="md:col-span-4 text-sm">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Protocol</div>
                    <div className="text-white/80 mt-0.5">{p.protocol}</div>
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Rest</div>
                    <div className="text-white/80 mt-0.5">{p.rest}</div>
                  </div>
                  <div className="md:col-span-12 border-t border-[var(--ink-border)] pt-3 text-sm text-white/60 italic">
                    {p.metrics}
                  </div>
                </>
              ) : (
                <>
                  <div className="md:col-span-3 space-y-2">
                    <Input value={p.phase} onChange={(e) => updatePhase(idx, "phase", e.target.value)} placeholder="Phase" className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white text-xs uppercase tracking-wider" data-testid={`phase-input-name-${idx}`} />
                    <Input value={p.block} onChange={(e) => updatePhase(idx, "block", e.target.value)} placeholder="Block" className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-semibold" data-testid={`phase-input-block-${idx}`} />
                    <input type="color" value={p.color} onChange={(e) => updatePhase(idx, "color", e.target.value)} className="h-9 w-full rounded-md border border-[var(--ink-border)] cursor-pointer bg-transparent" data-testid={`phase-input-color-${idx}`} />
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-1">Hybrid</div>
                    <Input value={p.hybrid} onChange={(e) => updatePhase(idx, "hybrid", e.target.value)} placeholder="A/B/C/D" className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-display font-black text-2xl" data-testid={`phase-input-hybrid-${idx}`} />
                  </div>
                  <div className="md:col-span-4">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-1">Protocol</div>
                    <Textarea value={p.protocol} onChange={(e) => updatePhase(idx, "protocol", e.target.value)} rows={3} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid={`phase-input-protocol-${idx}`} />
                  </div>
                  <div className="md:col-span-3">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-1">Rest</div>
                    <Textarea value={p.rest} onChange={(e) => updatePhase(idx, "rest", e.target.value)} rows={3} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid={`phase-input-rest-${idx}`} />
                  </div>
                  <div className="md:col-span-12">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-1">Metrics / KPIs</div>
                    <Textarea value={p.metrics} onChange={(e) => updatePhase(idx, "metrics", e.target.value)} rows={2} className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid={`phase-input-metrics-${idx}`} />
                  </div>
                  <div className="md:col-span-12 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removePhase(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`phase-delete-${idx}`}>
                      <Trash2 className="h-4 w-4 mr-2" /> Remove phase
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Button onClick={addPhase} variant="outline" className="w-full border-dashed border-white/20 text-white/70 bg-transparent hover:bg-white/5 py-6" data-testid="calendar-add-phase">
          <Plus className="h-4 w-4 mr-2" /> Add phase
        </Button>
      )}
    </div>
  );
}
